/**
 * ContractorLinesTable — the contractor estimate's lines, priced on screen by
 * calcContractorEstimate, editable in place. ONE table for Dispatch and the
 * Finalize tab's admin Contractor section (Finalize phase 2 §8.2): both edit the
 * same contractor_estimate_items rows, so they show the same lines the same way.
 * The caller owns the saving (useRowSync) and says whether the lines are editable.
 *
 * A line still following the client (local defaults, §9.3) is tagged "Auto";
 * editing it makes it the admin's and the tag goes.
 */

import React, { useEffect, useRef, useState } from 'react';
import { CRM } from '../lib/theme';
import { formatMoney } from '../lib/utils';
import { contractorBillableWeight, ContractorCalc, ContractorCalcLine, ContractorFinal } from '../lib/estimate';
import { Icons } from '../lib/icons';
import { numberInput, STEP_PER_LB } from '../lib/numberInput';

export const C = { ...CRM, mono: "'DM Mono', monospace" };

const SOURCE_LABEL: Record<string, string> = { base: 'Base', fee: 'Fee', custom: 'Custom' };
const td: React.CSSProperties = { padding: '7px 12px', fontSize: '12px', color: C.text, borderBottom: `1px solid ${C.panel2}` };
const iconBtn: React.CSSProperties = { background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: C.muted };

export interface ContractorLinesTableProps {
  calc: ContractorCalc;
  /** The stored row (with useRowSync's _key / _status / _error) for a line id. */
  rowOf: (id: string) => any;
  editable: boolean;
  busy: boolean;
  lead: any;
  final: ContractorFinal | null;
  total: number;
  /** A save is in flight: the total is not final yet. */
  saving: boolean;
  failed: number;
  onCommit: (id: string, patch: Record<string, any>) => void;
  onRemove: (id: string) => void;
  onRetry: (key: string) => void;
}

export function ContractorLinesTable({ calc, rowOf, editable, busy, lead, final, total, saving, failed, onCommit, onRemove, onRetry }: ContractorLinesTableProps) {
  const commitField = onCommit;
  const removeLine = onRemove;
  const contractorTotal = total;
  return (
    <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: '10px', overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {[
              { h: 'Description', a: 'left' },
              { h: 'Source', a: 'left' },
              { h: 'Rate', a: 'right' },
              { h: 'Qty', a: 'right' },
              { h: 'Amount', a: 'right' },
              { h: '', a: 'right' },
            ].map((col, i) => (
              <th key={col.h + i} style={{ textAlign: col.a as any, fontSize: '9px', fontWeight: 700, color: C.dim, textTransform: 'uppercase', padding: '9px 12px', borderBottom: `1px solid ${C.border}` }}>
                {col.h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {calc.items.length === 0 && (
            <tr><td colSpan={6} style={{ padding: '16px', textAlign: 'center', color: C.dim, fontSize: '12px' }}>No line items — add a fee or custom line.</td></tr>
          )}
          {calc.items.map((l) => {
            const st: any = l.id ? rowOf(l.id) : null;
            return (
            <tr key={st?._key ?? l.id} style={st?._status === 'error' ? { background: `${C.redTint}` } : undefined}>
              <td style={td}>
                {editable && l.source === 'custom' ? (
                  <TextField value={l.desc} disabled={busy} onCommit={(v) => commitField(l.id!, { description: v })} />
                ) : (
                  <span style={{ color: C.text }}>{l.desc}</span>
                )}
                {st?._status === 'error' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', fontSize: '11px', color: C.red }}>
                    <span>Not saved{st._error ? ` — ${st._error}` : ''}</span>
                    <button
                      onClick={() => onRetry(st._key)}
                      style={{ minHeight: '30px', padding: '4px 10px', background: 'transparent', border: `1px solid ${C.redLine}`, color: C.red, borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 700 }}
                    >
                      Retry
                    </button>
                  </div>
                )}
              </td>
              <td style={td}>
                <span style={{ fontSize: '10px', color: C.muted, border: `1px solid ${C.border}`, borderRadius: '4px', padding: '1px 7px' }}>
                  {SOURCE_LABEL[l.source] ?? l.source}
                </span>
                {st?.follows_client && (
                  <span title="Follows the client's figures (local defaults). Editing it makes it yours." style={{ marginLeft: '5px', fontSize: '10px', color: C.accent, border: `1px solid ${C.accentSoftLine}`, borderRadius: '4px', padding: '1px 7px' }}>Auto</span>
                )}
              </td>
              <td style={{ ...td, textAlign: 'right' }}>
                <RateCell l={l} editable={editable} busy={busy} onCommit={(patch) => commitField(l.id!, patch)} />
              </td>
              <td style={{ ...td, textAlign: 'right' }}>
                <QtyCell l={l} editable={editable} busy={busy} lead={lead} final={final} onCommit={(patch) => commitField(l.id!, patch)} />
              </td>
              <td style={{ ...td, textAlign: 'right' }}>
                <span style={{ fontFamily: C.mono, color: C.text }}>{formatMoney(l.net)}</span>
              </td>
              <td style={{ ...td, textAlign: 'right', width: '32px' }}>
                {editable && (
                  <button onClick={() => removeLine(l.id!)} disabled={busy} title="Remove line" aria-label="Remove line" style={{ ...iconBtn, color: C.red, width: '36px', height: '36px' }}><Icons.close size={15} /></button>
                )}
              </td>
            </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={4} style={{ ...td, borderTop: `2px solid ${C.border}`, borderBottom: 'none' }}>
              <span style={{ fontSize: '11px', color: C.dim }}>B2B — no tax, no discounts</span>
            </td>
            <td style={{ ...td, textAlign: 'right', borderTop: `2px solid ${C.border}`, borderBottom: 'none' }}>
              <span style={{ fontFamily: C.mono, fontSize: '15px', fontWeight: 700, color: C.green, opacity: saving || failed ? 0.55 : 1 }}>{formatMoney(contractorTotal)}</span>
              {/* Never presented as final while a line is unsaved. */}
              {(saving || failed > 0) && (
                <div style={{ fontSize: '10px', marginTop: '2px', color: failed ? C.red : C.amber }}>
                  {failed ? 'not final — a line did not save' : 'Updating…'}
                </div>
              )}
            </td>
            <td style={{ borderTop: `2px solid ${C.border}` }} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ---- Line cells: pick the editable field by kind ---------------------------
// Rate column: $/lb or $/hr (base), $/unit (unit fee), % (pct fee), or the flat
// $ (flat fee). Qty column: live weight (weight base, read-only), editable hours
// /units (hourly/unit/custom), or — for flat/pct.

export function RateCell({ l, editable, busy, onCommit }: { l: ContractorCalcLine; editable: boolean; busy: boolean; onCommit: (patch: Record<string, any>) => void }) {
  const suffix = l.rateKind === 'weight' ? '/lb' : l.rateKind === 'hourly' ? '/hr' : l.rateKind === 'unit' ? `/${l.unitLabel || 'unit'}` : '';
  const staticStyle: React.CSSProperties = { fontFamily: C.mono, color: C.text };
  const wrap = (input: React.ReactNode, suf: string) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', justifyContent: 'flex-end' }}>
      {input}{suf && <span style={{ color: C.muted, fontSize: '11px' }}>{suf}</span>}
    </span>
  );

  if (l.rateKind === 'pct') {
    return editable
      ? wrap(<NumField value={l.percentage} disabled={busy} onCommit={(n) => onCommit({ percentage: n })} width="70px" />, '%')
      : <span style={staticStyle}>{l.percentage}%</span>;
  }
  if (l.rateKind === 'flat') {
    return editable
      ? <NumField value={l.amount} disabled={busy} onCommit={(n) => onCommit({ amount: n })} />
      : <span style={staticStyle}>{formatMoney(l.amount)}</span>;
  }
  // weight / hourly / unit / money(custom) -> editable $ rate
  return editable
    ? wrap(<NumField value={l.rate} disabled={busy} onCommit={(n) => onCommit({ rate: n })} width="90px" step={l.rateKind === 'weight' ? STEP_PER_LB : 1} />, suffix)
    : <span style={staticStyle}>{formatMoney(l.rate)}{suffix}</span>;
}


export function QtyCell({ l, editable, busy, lead, final, onCommit }: { l: ContractorCalcLine; editable: boolean; busy: boolean; lead: any; final: ContractorFinal | null; onCommit: (patch: Record<string, any>) => void }) {
  // Weight base reads the lead's LIVE weight, floored to the CONTRACTOR minimum
  // (MW2) — not editable here (edit the weight on the lead), recomputes on change.
  // Once an invoice is issued (049) it is the invoice's final weight, still floored.
  if (l.rateKind === 'weight') {
    const w = final?.weightLbs != null ? l.quantity : contractorBillableWeight(lead);
    return <span style={{ fontFamily: C.mono, color: C.muted }} title={final?.weightLbs != null ? "The invoice's final weight" : undefined}>{w.toLocaleString()} lb</span>;
  }
  // Hourly base priced from the invoice: its final hours, not the stored figure.
  if (l.source === 'base' && l.rateKind === 'hourly' && final?.hours != null) {
    return <span style={{ fontFamily: C.mono, color: C.muted }} title="The invoice's final hours">{l.quantity} h{l.crewSize ? ` · crew ${l.crewSize}` : ''}</span>;
  }
  if (l.rateKind === 'pct' || l.rateKind === 'flat') {
    return <span style={{ color: C.dim }}>—</span>;
  }
  return editable
    ? <NumField value={l.quantity} disabled={busy} onCommit={(n) => onCommit({ quantity: n })} width="64px" />
    : <span style={{ fontFamily: C.mono, color: C.text }}>{l.quantity}</span>;
}

// ---- Commit-on-blur numeric field (consistent with inventory 8e.1) ---------

export function NumField({ value, disabled, onCommit, width = '110px', step = 1 }: { value: number; disabled?: boolean; onCommit: (n: number) => void; width?: string; step?: number }) {
  const [local, setLocal] = useState<string>(value === 0 ? '' : String(value));
  const lastExternal = useRef(value);
  useEffect(() => {
    if (value !== lastExternal.current) {
      lastExternal.current = value;
      setLocal(value === 0 ? '' : String(value));
    }
  }, [value]);
  const commit = () => {
    const n = Math.max(0, Number(local) || 0);
    setLocal(n === 0 ? '' : String(n));
    lastExternal.current = n;
    if (n !== value) onCommit(n);
  };
  return (
    <input
      {...numberInput(step)}
      value={local}
      placeholder="0"
      disabled={disabled}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
      style={{ width, textAlign: 'right', background: C.panel2, border: `1px solid ${C.border}`, color: C.text, borderRadius: '6px', padding: '5px 8px', fontSize: '12px', fontFamily: C.mono }}
    />
  );
}

export function TextField({ value, disabled, onCommit }: { value: string; disabled?: boolean; onCommit: (v: string) => void }) {
  const [local, setLocal] = useState<string>(value ?? '');
  const lastExternal = useRef(value);
  useEffect(() => {
    if (value !== lastExternal.current) {
      lastExternal.current = value;
      setLocal(value ?? '');
    }
  }, [value]);
  const commit = () => {
    lastExternal.current = local;
    if (local !== value) onCommit(local);
  };
  return (
    <input
      value={local}
      disabled={disabled}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
      style={{ width: '90%', background: C.panel2, border: `1px solid ${C.border}`, color: C.text, borderRadius: '6px', padding: '4px 8px', fontSize: '12px' }}
    />
  );
}
